'use client';

/**
 * AuthContext - Simplified Authentication State Management
 *
 * Responsibilities:
 * - User state management (login, logout, user data)
 * - Login/2FA flow
 * - User-initiated logout only
 *
 * NOT responsible for:
 * - Token expiry handling (axios-client handles this with redirect)
 * - Coordinating with HTTP client
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import axios from 'axios';
import * as Sentry from '@sentry/nextjs';
import axiosClient, { RequestConfig } from '@/lib/api/axios-client';
import { clientCookies } from '@/lib/auth/cookies';
import type {
  Usuario,
  AuthUsuarioDto,
  VerifyTwoAuthCodeDto,
  AuthResponse,
  Permission,
} from '@/types/api';

// ============================================================================
// TYPES
// ============================================================================

interface AuthContextType {
  user: Usuario | null;
  permissions: Permission[];
  isLoading: boolean;
  isAuthenticated: boolean;
  pendingVerificationEmail: string | null;
  login: (credentials: AuthUsuarioDto) => Promise<void>;
  verifyTwoAuthCode: (data: VerifyTwoAuthCodeDto) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  clearPendingVerification: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// STORAGE KEYS
// ============================================================================

const USER_KEY = 'naka_auth_user';
const PENDING_2FA_EMAIL_KEY = 'naka_pending_2fa_email';

// ============================================================================
// AUTH PROVIDER
// ============================================================================

export function AuthProvider({ children }: { children: ReactNode }) {
  // State
  const [user, setUser] = useState<Usuario | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null);

  const router = useRouter();

  // ============================================================================
  // LOGOUT FUNCTION - USER INITIATED ONLY
  // ============================================================================

  const logout = useCallback(() => {
    // Clear all auth state
    setUser(null);
    clientCookies.removeToken();
    localStorage.removeItem(USER_KEY);

    // Clear Sentry user context
    Sentry.setUser(null);

    // Show success message for user-initiated logout
    toast.success('Sesión cerrada exitosamente');

    // Redirect to login
    router.push('/login');
  }, [router]);

  // ============================================================================
  // ============================================================================
  // AUTH INITIALIZATION
  // ============================================================================

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = clientCookies.getToken();
        const storedUser = localStorage.getItem(USER_KEY);
        const storedPendingEmail = localStorage.getItem(PENDING_2FA_EMAIL_KEY);

        // Restore pending 2FA state if exists
        if (storedPendingEmail) {
          setPendingVerificationEmail(storedPendingEmail);
        }

        // If we have both token and user, validate with backend
        if (token && storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser) as Usuario;

            // Fetch fresh user data (with permissions) to validate token
            const response = await axiosClient.get<Usuario>(`/users/${parsedUser.idUser}`);
            const freshUser = response.data;

            setUser(freshUser);
            localStorage.setItem(USER_KEY, JSON.stringify(freshUser));

            // Set Sentry user context for error tracking (id + name + role)
            Sentry.setUser({
              id: String(response.data.idUser),
              username: `${response.data.nombre} ${response.data.apellido}`,
              // Custom data - cargo (role) for filtering errors by user type
              data: { cargo: response.data.cargo },
            });
          } catch (error) {
            // Token validation failed - clear everything
            // Note: If it was an auth error, axios-client already redirected
            console.error('Token validation failed during init:', error);
            clientCookies.removeToken();
            localStorage.removeItem(USER_KEY);
            setUser(null);
            Sentry.setUser(null);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // ============================================================================
  // LOGIN FUNCTION (STEP 1 OF 2FA)
  // ============================================================================

  const login = async (credentials: AuthUsuarioDto) => {
    try {
      // Call backend auth endpoint - returns message about 2FA code sent
      const response = await axiosClient.post<string>(
        '/users/auth',
        credentials,
        { skipAuth: true } as RequestConfig // Don't send token for login
      );

      // Store pending verification email
      setPendingVerificationEmail(credentials.email);
      localStorage.setItem(PENDING_2FA_EMAIL_KEY, credentials.email);

      // Show success message
      const message =
        response.data ||
        'Por favor, ingrese el código de verificación enviado a su correo electrónico';
      toast.success(message);

      // Navigation handled by login page component
    } catch (error) {
      console.error('Login error:', error);

      // Extract error message
      let errorMessage = 'Error al iniciar sesión';
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      throw error;
    }
  };

  // ============================================================================
  // 2FA VERIFICATION FUNCTION (STEP 2 OF 2FA)
  // ============================================================================

  const verifyTwoAuthCode = async (data: VerifyTwoAuthCodeDto) => {
    try {
      // Verify 2FA code with backend
      const response = await axiosClient.post<AuthResponse>(
        '/users/verify-two-auth-code',
        data,
        { skipAuth: true } as RequestConfig // Don't send token for verification
      );

      const { token, user: userData } = response.data;

      // Store token in cookie and user in localStorage
      clientCookies.setToken(token);
      setUser(userData);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      // Set Sentry user context for error tracking (id + name + role)
      Sentry.setUser({
        id: String(userData.idUser),
        username: `${userData.nombre} ${userData.apellido}`,
        data: { cargo: userData.cargo },
      });

      // Clear pending verification state
      setPendingVerificationEmail(null);
      localStorage.removeItem(PENDING_2FA_EMAIL_KEY);

      toast.success(`¡Bienvenido, ${userData.nombre}!`);

      // Navigation handled by verify page component
    } catch (error) {
      console.error('2FA verification error:', error);

      // Extract error message
      let errorMessage = 'Error al verificar código';
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      throw error;
    }
  };

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const clearPendingVerification = useCallback(() => {
    setPendingVerificationEmail(null);
    localStorage.removeItem(PENDING_2FA_EMAIL_KEY);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!user) return;

    try {
      const response = await axiosClient.get<Usuario>(`/users/${user.idUser}`);
      const freshUser = response.data;
      setUser(freshUser);
      localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
    } catch (error) {
      // If it was an auth error, axios-client already redirected
      // For other errors, just log
      console.error('Failed to refresh user:', error);
    }
  }, [user]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value: AuthContextType = {
    user,
    permissions: user?.Permissions ?? [],
    isLoading,
    isAuthenticated: !!user && !!clientCookies.getToken(),
    pendingVerificationEmail,
    login,
    verifyTwoAuthCode,
    logout,
    refreshUser,
    clearPendingVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
